// NOTE: imported from the concrete module rather than the `../types` barrel —
// `types/models.ts` re-exports from `../constants`, so going through the barrel
// would close a constants -> types -> constants loop. It is type-only and would
// be erased, but naming the file keeps the cycle from existing at all.
import type { IAssistant } from "../types/assistant";

// NOTE: NSAI-4909 — a `= []` destructuring default on the assistants query
// allocates a fresh array on every render for as long as the query has no
// data: while it is in flight, and permanently for a role that lacks
// `private_assistant:read` / `public_assistant:read`, where `GET /assistants`
// answers 403. That new identity invalidates every memo and effect keyed on
// the list, and the MOBILE chat screen re-derives its model selection inside
// such an effect — so that screen re-rendered without bound ("Maximum update
// depth exceeded"). Web carries the same shape but did not reproduce it, so
// its use of this constant is hardening only. Share one module-level empty
// list so an absent response keeps a stable reference. Not frozen on purpose:
// callers pass it into props typed as a mutable array, and a runtime freeze
// would turn any in-place use into a throw rather than the no-op it is today.
export const EMPTY_ASSISTANTS: IAssistant[] = [];

export const MAX_AVATAR_BYTES = 512 * 1024; // 512KB

export const ASSISTANT_SHARES_QUERY_KEY = "assistantShares";

// NOTE: NSAI-6814 — the Assistants page loads its list a page at a time. 30
// fills a tall desktop viewport with room to scroll (the server caps a page
// at 50). The cache key lives under ASSISTANTS_QUERY_KEY so every existing
// `invalidateQueries({ queryKey: [ASSISTANTS_QUERY_KEY] })` (create, delete,
// share, leave, project switch) refreshes the paged list too.
export const ASSISTANTS_PAGE_SIZE = 30;
export const ASSISTANTS_PAGE_QUERY_SCOPE = "page" as const;
// NOTE: Typing in the search box re-queries the server, so wait for a pause.
export const ASSISTANTS_SEARCH_DEBOUNCE_MS = 300;
// NOTE: The server's cap on the `search` param (MAX_ASSISTANTS_PAGE_SEARCH_LENGTH).
// Clients truncate to it before sending, so an over-long paste still searches
// instead of turning the list into a validation error.
export const ASSISTANTS_SEARCH_MAX_LENGTH = 512;

// NOTE: NSAI-3830 / NSAI-5274 — the organization assistant marketplace. The
// query key lives here rather than beside a client's hook because both clients
// drop it on a project switch: listings are project-scoped, so the next project
// must never paint the one just left.
export const MARKETPLACE_ASSISTANTS_QUERY_KEY = "marketplaceAssistants";

// NOTE: NSAI-3830 / NSAI-5274 — synthetic value for the marketplace tab on the
// assistants screen. Deliberately NOT part of EAssistantType: the tab is fed by
// GET /assistant-marketplace, not by a GET /assistants?type= filter. It also
// doubles as the `emptyState.marketplace` i18n key.
export const ASSISTANT_MARKETPLACE_TAB = "marketplace" as const;

// NOTE: backend only supports png, jpeg, gif, webp for assistant knowledge
export const ASSISTANT_IMAGE_FILE_ACCEPT: Record<string, string[]> = {
  "image/jpeg": [".jpg", ".jpeg"],
  "image/png": [".png"],
  "image/gif": [".gif"],
  "image/webp": [".webp"],
};
